import {ActionTree} from "vuex";
import {AxiosResponse, AxiosError} from "axios";
import moment from 'moment';
import IDataVariable from "@/modules/DataVariables/ts/IDataVariable";
import IDataVariableOption from "@/modules/DataVariables/ts/IDataVariableOption";
// @ts-ignore
import Api from "@/api.js";

export const actions: ActionTree<any, any> = {

    // TODO: These are only used from my profile page - data already loaded up to root store
    loadTimezones: ({state, getters, commit, dispatch}) => {

        commit('setTimezonesLoading', true);

        return new Promise((resolve, reject) => {

            if (state.timezones.length > 0) {
                resolve(state.timezones);
            }

            Api.get('/timezones')

                .then((response: AxiosResponse) => {

                    commit('setTimezones', response.data.timezones);
                    commit('setTimezonesLoading', false);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    commit('setTimezonesLoading', false);
                    reject(error);
                });
        });
    },

    updateLocaleDataVariable: ({state, getters, commit, dispatch}) => {
        return new Promise((resolve, reject) => {
            let found = state.dataVariables.find((variable: IDataVariable) => variable.name === 'LOCALE');

            // Data variables are not loaded, so need to refresh
            if (!found) resolve(null);

            dispatch('getDataVariable', found.id)
                .then(() => {
                    resolve(null);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    getDataVariable: ({state, getters, commit, dispatch}, id: number) => {
        return new Promise((resolve, reject) => {

            Api.get('/data-variable/' + id)
                .then((response: AxiosResponse) => {
                    commit('setDataVariable', response.data.dataVariable);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    getDataVariables: ({state, getters, commit, dispatch}, force) => {

        if (force) commit('bustDataVariableCache');

        return new Promise((resolve, reject) => {

            // Pull from store if loaded and cache hasn't expired
            if (state.dataVariablesExpire && moment().isBefore(state.dataVariablesExpire)) {
                resolve(state.dataVariables);
                return;
            }

            // Prevent double loading
            if (state.dataVariablesLoading) {
                for (let i = 0; i <= 10; i++) {
                    if (state.dataVariablesLoading) {
                        setTimeout(function () {
                        }, i * 100)
                    } else {
                        resolve(state.dataVariables);
                    }
                }
            } else {

                commit('setDataVariablesLoading', true);

                Api.get('/data-variables')

                    .then((response: AxiosResponse) => {
                        commit('setDataVariables', response.data.dataVariables);
                        resolve(response);
                    })
                    .catch((error: AxiosError) => {
                        reject(error);
                    })
                    .finally(() => {
                        commit('setDataVariablesLoading', false);
                })
            }
        });
    },

    getDataVariableOptions: ({state, getters, commit, dispatch}, id) => {

        return new Promise((resolve, reject) => {

            Api.get('/data-variable/' + id + '/options')

                .then(({data}: AxiosResponse) => {

                    if (data.options.length > 0) {

                        // Overwrite old order if not valid
                        let order = 1;
                        data.options.forEach((option: IDataVariableOption) => {
                            option.order = order;
                            order++;
                        });

                        commit('setDataVariableOptions', {id: id, options: data.options});
                    }

                    resolve(data.options);
                })
                .catch((error: AxiosError) => {

                    reject(error);
                });
        });
    },

    deleteDataVariable: ({state, getters, commit, dispatch}, dataVariableId) => {

        const backup = [...state.dataVariables];

        // Delete immediately
        commit('deleteDataVariable', dataVariableId);
        commit('setDataVariablesLoading', true);

        return new Promise((resolve, reject) => {

            Api.delete('/data-variable/' + dataVariableId)

                .then((response: AxiosResponse) => {

                    commit('setDataVariablesLoading', false);
                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    // Restore original object
                    commit('setDataVariables', backup);
                    commit('setDataVariablesLoading', false);
                    reject(error);
                });
        });

    },

    createDataVariable: ({state, getters, commit, dispatch}, dataVariable) => {

        return new Promise((resolve, reject) => {

            Api.post('/data-variables', dataVariable)

                .then((response: AxiosResponse) => {

                    commit('addDataVariable', response.data.dataVariable);

                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    updateDataVariable: ({state, getters, commit, dispatch, rootState}, dataVariable) => {

        const backup = rootState.dataVariables.find((variable: IDataVariable) => variable.id === dataVariable.id);

        commit('updateDataVariable', dataVariable);

        return new Promise((resolve, reject) => {

            Api.put('/data-variables', dataVariable)

                .then((response: AxiosResponse) => {

                    commit('updateDataVariable', response.data.dataVariable);

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    // Restore original object
                    commit('updateDataVariable', backup);
                    reject(error);
                });
        });
    },

    getDataVariableStats: ({state, getters, commit, dispatch, rootState}, {dataVariableId, subPanelIds}) => {

        return new Promise((resolve, reject) => {

            let url = '/data-variable/' + dataVariableId + '/stats';

            if (subPanelIds.length > 0) {
                url += '?sub_panels=' + subPanelIds.join(',');
            }

            Api.get(url)

                .then((response: AxiosResponse) => {
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    getDataVariableDataCount: ({state, getters, commit, dispatch, rootState}, {dataVariableId, subPanelIds}) => {

        return new Promise((resolve, reject) => {

            let url = '/data-variable/' + dataVariableId + '/count';

            if (subPanelIds?.length > 0) {
                url += '?sub_panels=' + subPanelIds.join(',');
            }

            Api.get(url)
                .then((response: AxiosResponse) => {
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    getDataVariableOptionDataCount: ({state, getters, commit, dispatch, rootState}, {dataVariableId, optionId}) => {

        return new Promise((resolve, reject) => {

            Api.get('/data-variable/' + dataVariableId + '/options/' + optionId + '/count')

                .then((response: AxiosResponse) => {
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    loadMessages: ({state, getters, commit, dispatch}, force) => {

        if (force) commit('bustMessagesCache');

        if (!state.expires || !moment().isBefore(state.expires)) {

            commit('setLoading', true);

            return new Promise((resolve, reject) => {

                Api.get('/announcements')

                    .then(({data}: AxiosResponse) => {
                        commit('setMessages', data.announcements);
                        commit('setLoading', false);
                        resolve(null);
                    })
                    .catch((error: AxiosError) => {
                        commit('setLoading', false);
                        reject(error);
                    });
            });
        }
    },

    loadAllAnnouncements: ({state, getters, commit, dispatch}, force) => {

        if (force) commit('bustAnnouncementsCache');
        if (!state.expiresAnnouncements || !moment().isBefore(state.expiresAnnouncements)) {

            commit('setLoading', true);

            return new Promise((resolve, reject) => {
                Api.get('/announcements/all')
                    .then(({data}: AxiosResponse) => {
                        commit('setAllAnnouncements', data.announcements);
                        commit('setLoading', false);
                        resolve(null);
                    })
                    .catch((error: AxiosError) => {
                        commit('setLoading', false);
                        reject(error);
                    });
            });
        }
    },

    markMessagesRead: ({state, getters, commit, dispatch}, data) => {

        return new Promise((resolve, reject) => {

            commit('removeMessages', data);

            Api.post('/announcements/read', {ids: data})

                .then(() => {
                    resolve(null);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    getACL: ({state, getters, commit, dispatch}) => {
        return new Promise((resolve, reject) => {

            Api.get('/user/acl')

                .then((response: AxiosResponse) => {
                    commit('saveAcl', {
                        permissions: response.data.permissions,
                        roles: response.data.roles
                    });
                    resolve(null);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    getActiveEngagedTopline: ({state, getters, commit, dispatch}, {force, subPanelFilterIds}) => {

        if (force) state.activeEngagedExpires = null;

        // Pull from store if loaded and cache hasn't expired
        if (state.activeEngagedExpires && moment().isBefore(state.activeEngagedExpires)) {
            return state.activeEngaged;
        }

        commit('setActiveEngagedLoading', true);

        return new Promise((resolve, reject) => {

            let url = '/health-metrics/active-engaged';

            let params = [];

            if (subPanelFilterIds.length > 0) {
                params.push('sub_panels=' + subPanelFilterIds.join(','));
            }

            if (force) {
                params.push('force=1');
            }

            if (params.length > 0) {
                url = url + '?' + params.join('&');
            }

            Api.get(url)

                .then(({data}: AxiosResponse) => {
                    commit('setActiveEngaged', data.topline);
                    commit('setActiveEngagedLoading', false);
                    resolve(data.topline);
                })
                .catch((error: AxiosError) => {
                    commit('setActiveEngagedLoading', false);
                    reject(error);
                });
        });
    },

    getAgeGender: ({state, getters, commit, dispatch}, force: boolean) => {

        if (force) state.ageGenderExpires = null;

        commit('setAgeGenderLoading', true);

        return new Promise((resolve, reject) => {

            if (state.ageGenderExpires && moment().isBefore(state.ageGenderExpires)) {
                commit('setAgeGenderLoading', false);
                resolve(state.ageGender);
            } else {
                Api.get('/health-metrics/age-vs-gender')

                    .then(({data}: AxiosResponse) => {
                        commit('setAgeGender', data.results.data);
                        commit('setAgeGenderLoading', false);
                        resolve(data.results.data);
                    })
                    .catch((error: AxiosError) => {
                        commit('setAgeGenderLoading', false);
                        reject(error);
                    });
            }
        });
    },

    getProductBoardUrl: ({state, getters, commit, dispatch}) => {

        return new Promise((resolve, reject) => {
            Api.get('/product-board')

                .then((response: AxiosResponse) => {
                    resolve(response.data.url);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });

        });
    },
}